import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, Trash2, ShoppingCart, CreditCard, ChevronLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { SecurityHeaders } from '@/components/SecurityHeaders';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import { GroupBuyTooltipBadge } from '@/components/groupbuy/GroupBuyTooltipBadge';
import { useShippingSettings } from '@/hooks/useShippingSettings';
import { CartItemDisplay } from '@/components/cart/CartItemDisplay';

const Cart = () => {
  const navigate = useNavigate();
  const {
    items,
    selectedItems,
    removeItem,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    toggleItemSelection,
    selectAllItems,
    getSelectedItemsCount,
    getSelectedItemsTotal,
    setCheckoutActive,
    clearCart,
  } = useCart();

  const [editingQuantity, setEditingQuantity] = useState<string | null>(null);
  const { settings: shippingSettings } = useShippingSettings();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const handleRemoveItem = (id: string) => {
    removeItem(id);
  };

  const handleQuantityChange = (id: string, newQuantity: number) => {
    const item = items.find(item => item.id === id);
    if (!item) return;

    const minQuantity = item.moq || 1;

    // For group buy products, don't apply inventory-based max limit
    const isGroupBuy = item.product.group_buy_enabled;

    if (newQuantity < minQuantity) {
      toast.error("כמות מינימלית", {
        description: `הכמות המינימלית להזמנה היא ${minQuantity}`,
      });
      return;
    }

    // For non-group-buy products, enforce maximum quantity based on inventory and per-order limits
    if (!isGroupBuy) {
      const maxQuantity = Math.min(
        item.maxOrderQuantity || item.product.availableQuantity,
        item.product.availableQuantity
      );

      if (newQuantity > maxQuantity) {
        toast.error("כמות מקסימלית", {
          description: `הכמות המקסימלית היא ${maxQuantity}`,
        });
        return;
      }
    }

    updateQuantity(id, newQuantity);
  };


  const handleSelectAll = () => {
    selectAllItems();
  };

  const handleProceedToCheckout = () => {
    if (getSelectedItemsCount() === 0) {
      toast.error("בחר פריטים", {
        description: "אנא בחר לפחות פריט אחד כדי להמשיך לתשלום",
      });
      return;
    }

    setCheckoutActive(true);
    navigate('/checkout');
  };

  const handleClearCart = () => {
    clearCart();
  };

  if (items.length === 0) {
    return (
      <>
        <SecurityHeaders />
        <Navigation />
        <div className="min-h-screen bg-background pt-8 lg:pt-8">
          <div className="container mx-auto px-4 py-2">
            {/* Breadcrumb */}
            <div className="mb-6" dir="rtl">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/">
                      בית
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator>
                    <ChevronLeft className="h-4 w-4" />
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    <BreadcrumbPage>עגלת קניות</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            <div className="max-w-2xl mx-auto text-center py-16">
              <div className="mb-6">
                <ShoppingCart className="h-24 w-24 mx-auto text-muted-foreground/50" />
              </div>
              <h1 className="text-2xl font-bold mb-4">העגלה שלך ריקה</h1>
              <p className="text-muted-foreground mb-8">
                נראה שלא הוספת עדיין מוצרים לעגלה. התחל לקנות ומצא מוצרים מעולים!
              </p>
              <Button asChild className="bg-lime text-lime-foreground hover:bg-lime/90">
                <Link to="/products">התחל קניות</Link>
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SecurityHeaders />
      <Navigation />
      <div className="min-h-screen bg-background pt-4 lg:pt-8">
        <div className="container mx-auto px-4 py-2">
          {/* Breadcrumb */}
          <div className="mb-6 mt-0" dir="rtl">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">
                    בית
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>
                  <ChevronLeft className="h-4 w-4" />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage>עגלת קניות</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between mb-6" dir="rtl">
            <div>
              <h1 className="text-3xl font-bold">עגלת קניות</h1>
              <p className="text-muted-foreground mt-1">
                {items.length} פריטים בעגלה
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleClearCart}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              נקה עגלה
            </Button>
          </div>

          <div className="flex flex-col lg:grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 order-1">
              {/* Select All */}
              <div className="flex items-center gap-3 mb-4 p-3 sm:p-4 border rounded-lg" dir="rtl">
                <Checkbox
                  checked={selectedItems.size === items.length && items.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="font-medium text-sm sm:text-base">בחר הכל ({items.length} פריטים)</span>
              </div>

              {/* Items List */}
              <div className="space-y-3 sm:space-y-4">
                {items.map((item) => (
                  <CartItemDisplay
                    key={item.id}
                    item={item}
                    isSelected={selectedItems.has(item.id)}
                    onToggleSelection={toggleItemSelection}
                    onRemove={handleRemoveItem}
                    onIncrement={incrementQuantity}
                    onDecrement={decrementQuantity}
                    isSidebar={false}
                  />
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1 order-2">
              <Card className="lg:sticky lg:top-24">
                <CardHeader>
                  <CardTitle className="text-right text-lg sm:text-xl">סיכום הזמנה</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4" dir="rtl">
                  <div className="flex justify-between text-sm sm:text-base">
                    <span>פריטים נבחרים:</span>
                    <span>{getSelectedItemsCount()}</span>
                  </div>

                  <div className="flex justify-between text-sm sm:text-base">
                    <span>סה"כ מוצרים:</span>
                    <span>{formatPrice(getSelectedItemsTotal())}</span>
                  </div>

                  <Separator />

                  <Alert>
                    <AlertDescription className="text-right text-xs sm:text-sm">
                      <p className="font-medium mb-1">משלוח והובלה:</p>
                      <p>• משלוח רגיל מ-₪{shippingSettings.centralPrice} (מרכז) / ₪{shippingSettings.remotePrice} (פריפריה)</p>
                      <p>• איסוף עצמי חינם מגנות הדר</p>
                      {getSelectedItemsTotal() < 1500 && (
                        <p className="mt-2 text-orange-600 font-medium">מינימום למשלוח ₪1,500 (איסוף עצמי בלבד בסכום הנוכחי)</p>
                      )}
                    </AlertDescription>
                  </Alert>

                  <div className="flex justify-between text-base sm:text-lg font-bold">
                    <span>סה"כ:</span>
                    <div className="text-left">
                      <span>{formatPrice(getSelectedItemsTotal())}</span>
                      <span className="block text-xs text-muted-foreground font-normal">כולל מע"מ</span>
                    </div>
                  </div>

                  <Button
                    className="w-full bg-lime text-lime-foreground hover:bg-lime/90 text-sm sm:text-base"
                    size="lg"
                    onClick={handleProceedToCheckout}
                    disabled={getSelectedItemsCount() === 0}
                  >
                    <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
                    המשך לתשלום ({getSelectedItemsCount()} פריטים)
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Cart;